import { useCallback } from 'react';
import { SessionForm } from '@components/Clinicians/session-form';
import { Form } from 'ALIASED-antd';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { isBlank } from '@helpers/htmlUtils';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { useClinicalSavedValues } from '../Clinical';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';

type MandatoryNotes = 'PLAN' | 'HPC' | 'UPDATE' | 'IMPRO';

export const useValidateClinicalTab = () => {
  const form = Form.useFormInstance<SessionForm>();
  const { data: appointmentMeta } = useAppointmentMeta();
  const { getNoteLabel, getIsFieldRequired } = useClinicalNotesMeta();
  const appt_id = useSessionAppointmentId();
  const { detailsCurrentAppointment } = useAppointmentDetails(appt_id);

  const mandatoryNotes = appointmentMeta?.appt_type_notes
    ?.filter(({ mandatory, note_code }) => mandatory && note_code !== 'DIAGNOSIS')
    .map(({ note_code }) => note_code);

  const savedValues = useClinicalSavedValues();

  const [, setAlert] = useSessionFormState('alert');

  const validateClinicalTab = useCallback(() => {
    const diagnosisErrors: string[] = [];

    mandatoryNotes?.forEach((section) => {
      const acceptedSection = section as MandatoryNotes;
      const noteContent = form.getFieldValue(acceptedSection) || savedValues[acceptedSection];
      if (isBlank(noteContent)) {
        diagnosisErrors.push(getNoteLabel(acceptedSection));
      }
    });

    const isDiagnosisMandatory = getIsFieldRequired('DIAGNOSIS');

    if (isDiagnosisMandatory && detailsCurrentAppointment) {
      if (!detailsCurrentAppointment.icd?.length) {
        diagnosisErrors.push('ICD11 Diagnosis');
      }
      if (!detailsCurrentAppointment.formal_diagnosis) {
        diagnosisErrors.push('Diagnosis Type');
      }
    }

    if (diagnosisErrors.length) {
      const errors = diagnosisErrors.join(', ');
      const message = 'Please fill in the missing required fields in the "Clinical" tab';

      return { isValid: false, invalidMessage: errors ? `${message}: ${errors}` : message };
    }
    return { isValid: true };
  }, [
    form,
    mandatoryNotes,
    savedValues.HPC,
    savedValues.IMPRO,
    savedValues.PLAN,
    savedValues.UPDATE,
    detailsCurrentAppointment,
    setAlert
  ]);
  return validateClinicalTab;
};
